import { Box, Button, InlineStack, Link, Select, Stack, Text } from '@customerio/pluma-components/react';
import { useBuilderActions, useMeta, useTree, useViewForNode, type LeafViewComponent } from '@customerio/dryad-react';
import * as styles from './condition-group.css';

const GROUP_OPERATOR_OPTIONS = [
  { value: 'and', label: 'All' },
  { value: 'or', label: 'At least one' },
];

// A registered view can opt out of the group's shared row chrome by declaring
// a static `ownsRowChrome = true`: the group then renders it without the
// shared duplicate/remove buttons, and the row supplies its own affordances
// (via removeNode). For rows whose surface is a self-contained Pluma
// component (a read-only ConditionViewer sentence, say), the shared X would
// double up with the one the viewer's actions slot carries, and a duplicate of
// a display-only condition isn't editable anyway.
function viewOwnsRowChrome(view: LeafViewComponent | undefined): boolean {
  return view != null && 'ownsRowChrome' in view && view.ownsRowChrome === true;
}

type GroupItemRowProps = {
  rowId: string;
  rowKind: 'leaf' | 'group' | 'error';
  rowType: string | null;
  index: number;
  /** Whether the group renders the AND/OR gutter (more than one row). */
  showGutter: boolean;
  operator: string;
};

function GroupItemRow({ rowId, rowKind, rowType, index, showGutter, operator }: GroupItemRowProps) {
  const meta = useMeta();
  const { duplicateNode, removeNode } = useBuilderActions();
  const View = useViewForNode(rowType ?? '');
  const ownsChrome = rowKind === 'leaf' && viewOwnsRowChrome(View);

  return (
    <InlineStack data-testid={`dryad-group-item-${index}`} gap="100" alignBlock="start">
      {showGutter ? (
        <Box as="span" className={styles.operatorGutter}>
          {index !== 0 ? (
            <Box as="span" className={styles.logicalOperatorLabel} data-testid="dryad-logical-operator">
              {operator}
            </Box>
          ) : null}
        </Box>
      ) : null}
      <Box className={ownsChrome ? undefined : styles.itemCard} flex={1} minW="0">
        <Box flex={1} minW="0">
          {/* Error nodes (wire no plugin claims) and leaf types whose row hasn't
              been ported yet render read-only but stay removable, so a saved
              filter can't get stuck. */}
          {rowKind === 'leaf' && View != null ? (
            <View id={rowId} />
          ) : (
            <Text color="subtle">This condition can't be edited here yet.</Text>
          )}
        </Box>
        {ownsChrome ? null : (
          <>
            <Button
              data-testid="dryad-row-duplicate"
              aria-label="Duplicate condition"
              icon="duplicate"
              isIconOnly
              variant="tertiary"
              size="sm"
              onClick={() => duplicateNode(rowId)}
              isDisabled={meta.isDisabled}
            />
            <Button
              data-testid="dryad-row-remove"
              aria-label="Remove condition"
              icon="close"
              isIconOnly
              variant="tertiary"
              size="sm"
              onClick={() => removeNode(rowId)}
              isDisabled={meta.isDisabled}
            />
          </>
        )}
      </Box>
    </InlineStack>
  );
}

/**
 * Clears the tree back to an empty AND group. A filter-page affordance, not a
 * property of every group — a surface that wants it renders it in the slot
 * `ConditionGroup` exposes, and the rest never see it.
 */
export function ClearConditionsLink() {
  const meta = useMeta();
  const tree = useTree();
  const { setTree } = useBuilderActions();

  if (tree.children.length === 0) {
    return null;
  }

  return (
    <Link
      data-testid="dryad-clear-filters"
      onClick={() => setTree({ kind: 'group', operator: 'and', children: [] })}
      isDisabled={meta.isDisabled}
    >
      Clear filters
    </Link>
  );
}

export type ConditionGroupProps = {
  /**
   * Narrows a picked menu value to a type the surface has a row for; returning
   * null ignores the pick. Keeps an unported type out of the tree.
   */
  conditionTypeOf: (value: string) => string | null;
  /**
   * Rendered beside the all/any select — where a surface puts Clear Filters or
   * anything else that belongs to the group header rather than to a row.
   */
  headerActions?: React.ReactNode;
};

/**
 * One group's chrome: the all/any operator, the rows with their AND/OR gutter,
 * and the add-condition menu. Reads the builder through dryad's context hooks,
 * so it works under any surface's provider.
 *
 * Renders a single level. Nesting is a separate decision — both surfaces run at
 * maxDepth 1 today, and dryad's `canAddGroup` is the seam for adding it later.
 */
export function ConditionGroup({ conditionTypeOf, headerActions }: ConditionGroupProps) {
  const tree = useTree();
  const meta = useMeta();
  const { addNode, setOperator } = useBuilderActions();

  const handleAdd = (value: string | null) => {
    if (value == null) {
      return;
    }
    const type = conditionTypeOf(value);
    if (type != null) {
      addNode(tree.id, type);
    }
  };

  const rows = tree.children;

  return (
    <Stack gap="100">
      <InlineStack data-testid="dryad-group-operator" alignBlock="center" gap="100">
        <Select
          ariaLabel="Group operator"
          size="sm"
          value={tree.operator}
          options={GROUP_OPERATOR_OPTIONS}
          onChange={(value: string | null) => {
            if (value === 'and' || value === 'or') {
              setOperator(tree.id, value);
            }
          }}
          isDisabled={meta.isDisabled}
        />
        <Text color="subtle">of the following conditions match</Text>
        {headerActions}
      </InlineStack>

      {rows.length > 0 ? (
        <Stack gap="100">
          {rows.map((row, index) => (
            <GroupItemRow
              key={row.id}
              rowId={row.id}
              rowKind={row.kind}
              rowType={row.kind === 'leaf' ? row.type : null}
              index={index}
              showGutter={rows.length > 1}
              operator={tree.operator}
            />
          ))}
        </Stack>
      ) : null}

      <InlineStack alignBlock="center" gap="100">
        {/* A constant empty value keeps the select on its placeholder after each pick. */}
        <Select
          data-testid="dryad-group-add"
          ariaLabel="Add condition"
          size="sm"
          value=""
          placeholder="Add condition"
          options={meta.typeOptions}
          onChange={handleAdd}
          isDisabled={meta.isDisabled}
        />
      </InlineStack>
    </Stack>
  );
}
